/**
 * React hook for managing legal video consultations
 * 
 * Provides state management and real-time updates for legal video calls
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  legalVideoService, 
  LegalConsultationRoom, 
  LegalParticipant, 
  LegalConsultationStatus,
  CreateLegalConsultationRequest 
} from '../services/legalVideoService';

export interface VideoState {
  localVideo: boolean;
  localAudio: boolean;
  screenShare: boolean;
  recording: boolean;
}

export interface ConsultationState {
  room: LegalConsultationRoom | null;
  participant: LegalParticipant | null;
  status: LegalConsultationStatus | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  videoState: VideoState;
  consultationTime: number;
  recordingConsent: boolean;
}

export interface UseLegalVideoConsultationProps {
  autoJoin?: boolean;
  onConsultationEnd?: (billingSummary: any) => void;
  onError?: (error: string) => void;
  onParticipantJoined?: (participant: any) => void;
  onParticipantLeft?: (participant: any) => void;
}

export const useLegalVideoConsultation = (props: UseLegalVideoConsultationProps = {}) => {
  const {
    autoJoin = false,
    onConsultationEnd,
    onError,
    onParticipantJoined,
    onParticipantLeft
  } = props;

  const [state, setState] = useState<ConsultationState>({
    room: null,
    participant: null,
    status: null,
    isConnected: false,
    isLoading: false,
    error: null,
    videoState: {
      localVideo: true,
      localAudio: true,
      screenShare: false,
      recording: false
    },
    consultationTime: 0,
    recordingConsent: false
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Update state helper
  const updateState = useCallback((updates: Partial<ConsultationState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Start consultation timer
  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    
    timerRef.current = setInterval(() => {
      setState(prev => ({ 
        ...prev, 
        consultationTime: prev.consultationTime + 1 
      }));
    }, 1000);
  }, []);

  // Stop consultation timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start polling room status
  const startStatusPolling = useCallback((roomId: string) => {
    if (statusPollingRef.current) return;

    const pollStatus = async () => {
      try {
        const status = await legalVideoService.getConsultationStatus(roomId);
        updateState({ status });
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    // Poll every 5 seconds
    statusPollingRef.current = setInterval(pollStatus, 5000);
    pollStatus(); // Initial poll
  }, [updateState]);

  // Stop polling room status
  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  }, []);

  // Create a new consultation room
  const createConsultation = useCallback(async (request: CreateLegalConsultationRequest): Promise<string> => {
    try {
      updateState({ isLoading: true, error: null });

      console.log('[useLegalVideo] Creating consultation for:', request.clientName);
      const room = await legalVideoService.createConsultation(request);
      
      updateState({ 
        room, 
        isLoading: false,
        recordingConsent: request.recordingRequested || false
      });

      return room.roomId;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create consultation';
      updateState({ error: errorMessage, isLoading: false });
      onError?.(errorMessage);
      throw error;
    }
  }, [updateState, onError]);

  // Join as attorney
  const joinAsAttorney = useCallback(async (roomId: string, attorneyName: string): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      console.log('[useLegalVideo] Attorney joining room:', roomId);
      const participant = await legalVideoService.createAttorneyToken(roomId, attorneyName);
      
      updateState({ 
        participant, 
        isConnected: true, 
        isLoading: false 
      });

      startTimer();
      startStatusPolling(roomId);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to join as attorney';
      updateState({ error: errorMessage, isLoading: false });
      onError?.(errorMessage);
      throw error;
    }
  }, [updateState, onError, startTimer, startStatusPolling]);

  // Join as client
  const joinAsClient = useCallback(async (roomId: string, clientName: string): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      console.log('[useLegalVideo] Client joining room:', roomId);
      const participant = await legalVideoService.createClientToken(roomId, clientName);
      
      updateState({ 
        participant, 
        isConnected: true, 
        isLoading: false 
      });

      startTimer();
      startStatusPolling(roomId);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to join as client';
      updateState({ error: errorMessage, isLoading: false });
      onError?.(errorMessage);
      throw error;
    }
  }, [updateState, onError, startTimer, startStatusPolling]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    setState(prev => {
      const newVideoState = !prev.videoState.localVideo;
      console.log('[useLegalVideo] Video toggled:', newVideoState);
      return {
        ...prev,
        videoState: {
          ...prev.videoState,
          localVideo: newVideoState
        }
      };
    });
    
    // TODO: Implement actual video toggle with WebRTC
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    setState(prev => {
      const newAudioState = !prev.videoState.localAudio;
      console.log('[useLegalVideo] Audio toggled:', newAudioState);
      return {
        ...prev,
        videoState: {
          ...prev.videoState,
          localAudio: newAudioState
        }
      };
    });
    
    // TODO: Implement actual audio toggle with WebRTC
  }, []);

  // Toggle screen share
  const toggleScreenShare = useCallback(async () => {
    try {
      const newScreenShareState = !state.videoState.screenShare;
      
      updateState({
        videoState: {
          ...state.videoState,
          screenShare: newScreenShareState
        }
      });

      // TODO: Implement actual screen share with WebRTC
      console.log('[useLegalVideo] Screen share toggled:', newScreenShareState);

    } catch (error) {
      console.error('Error toggling screen share:', error);
      onError?.('Failed to toggle screen share');
    }
  }, [state.videoState, updateState, onError]);

  // Start/stop recording with consent check
  const toggleRecording = useCallback(async () => {
    try {
      if (!state.videoState.recording && !state.recordingConsent) {
        onError?.('Recording requires client consent');
        return;
      }

      const newRecordingState = !state.videoState.recording;
      
      updateState({
        videoState: {
          ...state.videoState,
          recording: newRecordingState
        }
      });

      if (state.room) {
        await legalVideoService.updateRecordingConsent(state.room.roomId, state.recordingConsent);
      }

      console.log('[useLegalVideo] Recording toggled:', newRecordingState);

    } catch (error) {
      console.error('Error toggling recording:', error);
      onError?.('Failed to toggle recording');
    }
  }, [state.videoState.recording, state.recordingConsent, state.room, updateState, onError]);

  // Update recording consent
  const updateRecordingConsent = useCallback(async (consent: boolean) => {
    try {
      updateState({ recordingConsent: consent });
      
      if (state.room) {
        await legalVideoService.updateRecordingConsent(state.room.roomId, consent);
      }

      console.log('[useLegalVideo] Recording consent updated:', consent);

    } catch (error) {
      console.error('Error updating recording consent:', error);
      onError?.('Failed to update recording consent');
    }
  }, [state.room, updateState, onError]);

  // End consultation
  const endConsultation = useCallback(async (): Promise<void> => {
    try {
      if (!state.room) {
        throw new Error('No active consultation to end');
      }

      updateState({ isLoading: true });
      
      const result = await legalVideoService.endConsultation(state.room.roomId);
      
      stopTimer();
      stopStatusPolling();
      
      updateState({ 
        isConnected: false, 
        isLoading: false 
      });

      onConsultationEnd?.(result.billingSummary);
      console.log('[useLegalVideo] Consultation ended:', result);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to end consultation';
      updateState({ error: errorMessage, isLoading: false });
      onError?.(errorMessage);
      throw error;
    }
  }, [state.room, updateState, onConsultationEnd, onError, stopTimer, stopStatusPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      stopStatusPolling();
    };
  }, [stopTimer, stopStatusPolling]);

  // Format consultation time
  const formatTime = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return hours > 0 
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Calculate billable amount
  const getBillableAmount = useCallback((): number => {
    if (!state.room) return 0;
    const hours = state.consultationTime / 3600;
    return Math.round(hours * state.room.billableRate * 100) / 100;
  }, [state.consultationTime, state.room]);

  return {
    // State
    ...state,
    
    // Computed values
    formattedTime: formatTime(state.consultationTime),
    billableAmount: getBillableAmount(),
    
    // Actions
    createConsultation,
    joinAsAttorney,
    joinAsClient,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    toggleRecording,
    updateRecordingConsent,
    endConsultation,
    
    // Utility
    formatTime,
    getBillableAmount
  };
};

export default useLegalVideoConsultation;